import type { Breadcrumb, Event } from "@sentry/angular";
import { loggablePath } from "./url-path";

const DROPPED_BREADCRUMB_CATEGORIES = new Set(["console"]);
const URL_BREADCRUMB_FIELDS = ["url", "from", "to"];
const KEPT_BREADCRUMB_FIELDS = ["method", "status_code"];

const DROPPED_SPAN_FIELDS = [
    "http.query",
    "url.query",
    "http.client_ip",
    "client.address",
    "net.peer.ip",
    "net.sock.peer.addr",
];
const URL_SPAN_FIELDS = ["http.url", "http.target", "url.full", "url"];

type SpanData = Record<string, unknown> | undefined;

function scrubSpanData(data: SpanData): SpanData {
    if (!data) {
        return data;
    }
    const scrubbed = { ...data };
    for (const field of DROPPED_SPAN_FIELDS) {
        delete scrubbed[field];
    }
    for (const field of URL_SPAN_FIELDS) {
        if (typeof scrubbed[field] === "string") {
            scrubbed[field] = loggablePath(scrubbed[field] as string);
        }
    }
    return scrubbed;
}

export function scrubSentryEvent<T extends Event>(event: T): T {
    const { request, user, contexts, spans } = event;
    return {
        ...event,
        transaction: loggablePath(event.transaction),
        request: request && {
            method: request.method,
            url: loggablePath(request.url),
        },
        user: user && { id: user.id },
        contexts: contexts && {
            ...contexts,
            trace: contexts.trace && {
                ...contexts.trace,
                data: scrubSpanData(contexts.trace.data),
            },
        },
        spans: spans?.map((span) => ({
            ...span,
            description: loggablePath(span.description),
            data: scrubSpanData(span.data),
        })),
    };
}

export function scrubSentryBreadcrumb(
    breadcrumb: Breadcrumb,
): Breadcrumb | null {
    if (DROPPED_BREADCRUMB_CATEGORIES.has(breadcrumb.category)) {
        return null;
    }
    return { ...breadcrumb, data: scrubBreadcrumbData(breadcrumb.data) };
}

function scrubBreadcrumbData(
    data: Breadcrumb["data"],
): Breadcrumb["data"] | undefined {
    if (!data) {
        return undefined;
    }
    const scrubbed: Breadcrumb["data"] = {};
    for (const field of KEPT_BREADCRUMB_FIELDS) {
        if (field in data) {
            scrubbed[field] = data[field];
        }
    }
    for (const field of URL_BREADCRUMB_FIELDS) {
        if (typeof data[field] === "string") {
            scrubbed[field] = loggablePath(data[field]);
        }
    }
    return scrubbed;
}
